import { useState, useEffect } from "react";
import { Link, useLocation } from "wouter";
import { Droplets, Mail, Lock, Loader2, AlertCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { useAuth } from "@/contexts/AuthContext";
import { ThemeToggle } from "@/components/ThemeToggle";
import { useToast } from "@/hooks/use-toast";
import { trackLogin } from "@/lib/analytics";

export default function Login() {
  const [, setLocation] = useLocation();
  const { signInWithEmail, signIn, user } = useAuth();
  const { toast } = useToast();
  
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [rememberMe, setRememberMe] = useState(true); // Default to true
  const [loading, setLoading] = useState(false);
  const [redirectError, setRedirectError] = useState<string | null>(null);

  // Check for redirect errors and auto-redirect if already logged in (with role-based routing)
  useEffect(() => {
    // Check for both redirect error and auth error
    const storedLoginError = localStorage.getItem('login_error');
    const storedAuthError = localStorage.getItem('auth_error');
    const storedError = storedLoginError || storedAuthError;
    
    if (storedError) {
      try {
        const errorData = JSON.parse(storedError);
        // Only show if error is recent (within last 30 seconds)
        if (Date.now() - errorData.timestamp < 30000) {
          let errorMessage = "Giriş yapılırken bir hata oluştu";
          
          if (errorData.code === "auth/popup-closed-by-user") {
            errorMessage = "Giriş penceresi kapatıldı";
          } else if (errorData.code === "auth/network-request-failed") {
            errorMessage = "İnternet bağlantınızı kontrol edin (Hata: Ağ hatası)";
          } else if (errorData.code === "auth/unauthorized-domain") {
            errorMessage = "Bu domain Google OAuth için yetkilendirilmemiş (Hata: " + errorData.code + ")";
          } else if (errorData.code === "permission-denied") {
            errorMessage = "Veritabanı izin hatası. Lütfen daha sonra tekrar deneyin (Hata: " + errorData.code + ")";
          } else if (errorData.code) {
            errorMessage = `Giriş hatası: ${errorData.code}`;
          }
          
          setRedirectError(errorMessage);
          toast({
            title: "Hata",
            description: errorMessage,
            variant: "destructive",
          });
        }
      } catch (e) {
      } finally {
        localStorage.removeItem('login_error');
        localStorage.removeItem('auth_error');
      }
    }
    
    // Auto-redirect if already logged in (role-based)
    if (user) {
      const roleBasedPath = user.role === 'admin' ? '/admin' :
                           user.role === 'employee' ? '/employee' :
                           user.role === 'cashier' ? '/cashier' :
                           '/customer';
      setLocation(roleBasedPath);
    }
  }, [user, setLocation, toast]);

  const handleEmailSignIn = async (e: React.FormEvent) => {
    e.preventDefault();
    setRedirectError(null);
    
    setLoading(true);
    try {
      const userData = await signInWithEmail(email, password, rememberMe);
      
      // Track successful login
      trackLogin('email');
      
      toast({
        title: "Başarılı",
        description: "Giriş yapıldı! Yönlendiriliyorsunuz...",
      });
      
      // Use returned user data for immediate role-based navigation
      if (userData) {
        const roleBasedPath = userData.role === 'admin' ? '/admin' :
                             userData.role === 'employee' ? '/employee' :
                             userData.role === 'cashier' ? '/cashier' :
                             '/customer';
        
        // Small delay to show success message
        setTimeout(() => {
          setLocation(roleBasedPath);
        }, 500);
      } else {
        // Fallback if no user data
        setTimeout(() => {
          setLocation("/customer");
        }, 500);
      }
    } catch (error: any) {
      let errorMessage = "Giriş yapılırken bir hata oluştu";
      
      if (error.code === "auth/user-not-found") {
        errorMessage = "Bu e-posta adresi ile kayıtlı kullanıcı bulunamadı (Hata: auth/user-not-found)";
      } else if (error.code === "auth/wrong-password") {
        errorMessage = "Hatalı şifre (Hata: auth/wrong-password)";
      } else if (error.code === "auth/invalid-email") {
        errorMessage = "Geçersiz e-posta adresi (Hata: auth/invalid-email)";
      } else if (error.code === "auth/invalid-credential") {
        errorMessage = "E-posta veya şifre hatalı (Hata: auth/invalid-credential)";
      } else if (error.code) {
        errorMessage = `Giriş hatası: ${error.code}`;
      }
      
      toast({
        title: "Hata",
        description: errorMessage,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSignIn = async () => {
    setRedirectError(null);
    setLoading(true);
    try {
      // signIn now tries popup first, falls back to redirect if blocked
      const userData = await signIn(rememberMe);
      
      // If we got user data back, popup was successful - navigate immediately
      if (userData) {
        toast({
          title: "Başarılı",
          description: "Google ile giriş yapıldı!",
        });
        
        const roleBasedPath = userData.role === 'admin' ? '/admin' :
                             userData.role === 'employee' ? '/employee' :
                             userData.role === 'cashier' ? '/cashier' :
                             '/customer';
        
        setTimeout(() => {
          setLocation(roleBasedPath);
        }, 300);
        return;
      }
      
      // If null returned, redirect flow started - show message
      toast({
        title: "Yönlendiriliyor",
        description: "Google hesabınızla giriş yapılıyor...",
      });
    } catch (error: any) {
      let errorMessage = "Google ile giriş yapılırken bir hata oluştu";
      
      if (error.code) {
        errorMessage += ` (Hata: ${error.code})`;
      }
      
      toast({
        title: "Hata",
        description: errorMessage,
        variant: "destructive",
      });
      setRedirectError(errorMessage);
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex flex-col">
      {/* Navigation */}
      <nav className="border-b border-border bg-card">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <Link href="/">
              <div className="flex items-center gap-2 cursor-pointer hover-elevate active-elevate-2 px-3 py-2 rounded-lg transition-all" data-testid="link-home">
                <Droplets className="w-8 h-8 text-primary" />
                <span className="text-xl font-bold text-primary">TemizFilo</span>
              </div>
            </Link>
            <ThemeToggle />
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <div className="flex-1 flex items-center justify-center px-4 py-12">
        <Card className="w-full max-w-md p-8 rounded-2xl">
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-primary/10 mb-4">
              <Droplets className="w-8 h-8 text-primary" />
            </div>
            <h1 className="text-3xl font-bold mb-2 text-primary">🫧 Giriş Yap 🫧</h1>
            <p className="text-muted-foreground">
              Hesabınıza giriş yapın ve randevularınızı yönetin
            </p>
          </div>

          {redirectError && (
            <Alert variant="destructive" className="mb-4">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{redirectError}</AlertDescription>
            </Alert>
          )}

          <form onSubmit={handleEmailSignIn} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email">E-posta</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                <Input
                  id="email"
                  type="email"
                  placeholder="ornek@email.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="pl-10"
                  required
                  data-testid="input-email"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="password">Şifre</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                <Input
                  id="password"
                  type="password"
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="pl-10"
                  required
                  data-testid="input-password"
                />
              </div>
            </div>

            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="remember-me"
                  checked={rememberMe}
                  onChange={(e) => setRememberMe(e.target.checked)}
                className="w-4 h-4"
                data-testid="checkbox-remember-me"
              />
              <Label htmlFor="remember-me" className="cursor-pointer text-sm">
                Oturumu açık bırak
              </Label>
              </div>
              
              <Link href="/forgot-password">
                <span className="text-sm text-primary hover:underline cursor-pointer" data-testid="link-forgot-password">
                  Şifremi Unuttum
                </span>
              </Link>
            </div>

            <Button
              type="submit"
              className="w-full"
              disabled={loading}
              data-testid="button-login"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Giriş Yapılıyor...
                </>
              ) : (
                "Giriş Yap"
              )}
            </Button>
          </form>

          <div className="relative my-6">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-border"></div>
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-2 bg-card text-muted-foreground">veya</span>
            </div>
          </div>

          <Button
            variant="outline"
            className="w-full"
            onClick={handleGoogleSignIn}
            data-testid="button-google-signin"
          >
            <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
              <path
                fill="currentColor"
                d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
              />
              <path
                fill="currentColor"
                d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
              />
              <path
                fill="currentColor"
                d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
              />
              <path
                fill="currentColor"
                d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
              />
            </svg>
            Google ile Giriş Yap
          </Button>

          <div className="mt-6 text-center text-sm">
            <span className="text-muted-foreground">Hesabınız yok mu? </span>
            <Link href="/register">
              <a className="text-primary font-semibold hover:underline" data-testid="link-register">
                Kayıt Ol
              </a>
            </Link>
          </div>
        </Card>
      </div>
    </div>
  );
}
